<script setup>
import {onMounted, reactive, ref} from 'vue'
import {clone, State, can, go, titlecase} from '@/paks/vu-app'
import {getDisplay, getViewChildren} from './DashUtils.js'
import PageEdit from './PageEdit.vue'
import SelectDisplay from './SelectDisplay.vue'

const Fields = [
    {name: 'edit', icon: '$edit', width: '5%'},
    {
        name: 'design',
        icon: (v, rec) => (rec.component == 'Page' ? '$dashboard' : ''),
        width: '5%',
    },
    {name: 'show', icon: '$launch', width: '5%'},
    {name: 'icon', icon: (v, rec) => (rec.icon ? rec.icon : ''), desktop: true},
    {
        name: 'tab',
        title: 'Tab Group',
        icon: (v, rec) => (Array.isArray(rec.tabs) && rec.tabs.length == 0 ? '$check' : ''),
        width: '5%',
        desktop: true,
    },
    {name: 'path'},
    {name: 'menu', desktop: true},
    {name: 'component', desktop: true},
    {name: 'children', format: (v, rec) => getViewChildren(rec.views).length, desktop: true},
]

const page = reactive({
    builder: State.config.builder,
    display: {},
    displays: [],
    displayName: null,
    fields: Fields,
    designPageEdit: false,
    showNested: false,
    showPageEdit: false,
    view: null,
    views: [],
})

const table = ref(null)

onMounted(() => {
    page.select = {
        actions: {Add: 0, Edit: 1, Delete: 2},
        multi: true,
        property: 'select',
    }
    page.displayName = State.app.display.name
    page.displays = State.app.fullDisplay.displays || []
    page.display = getDisplay(page.displayName)
    page.views = makeViews(clone(page.display.views))
})

function changeDisplay(displayName) {
    page.displayName = displayName
    page.display = getDisplay(displayName)
    page.views = makeViews(clone(page.display.views))
}

function makeViews(views, result = [], parent) {
    for (let view of views) {
        result.push(view)
        if (parent?.tabs) {
            view.tab = titlecase(view.tab || view.name || view.path)
        }
        if (view.views && (view.path == '/' || page.showNested)) {
            makeViews(view.views, result, view)
        }
    }
    return result
}

async function clicked({action, item, items, column, rec, row}) {
    if (action == 'add') {
        if (!can('admin')) {
            return
        }
        editPage({})
    } else if (action == 'edit' || (action == 'cell' && column.name == 'edit')) {
        if (can('admin')) {
            editPage(item)
        }
    } else if (action == 'cell') {
        if (column.name == 'design') {
            if (item.component == 'Page') {
                State.ref.router.push({name: 'page-editor', query: {path: item.path, design: true}}).catch(() => {})
            }
        } else if (column.name == 'show') {
            go(item.path)
        }
    }
}

async function editPage(view) {
    page.showPageEdit = view ? true : false
    page.view = view ? Object.assign({}, view) : null
    reload()
    window.scrollTo({top: 0, behavior: 'smooth'})
}

async function reload() {
    page.display = getDisplay(page.displayName)
    page.views = makeViews(clone(page.display.views))
    if (table.value) {
        await table.value.update()
    }
}

function changeShowNested() {
    page.views = makeViews(clone(page.display.views))
}
</script>

<template>
    <div class="view page page-list">
        <vu-help url="/doc/ui/view/view-list.html" />

        <SelectDisplay v-if="page.displays.length > 1" v-model="page.displayName" @input="changeDisplay" />

        <vu-table
            :data="page.views"
            :fields="page.fields"
            :select="page.select"
            :pageSize="State.app.mobile ? 14 : 200"
            name="pages"
            nodata=" "
            options="filter,toolbar"
            ref="table"
            sort="path:asc"
            width="100%"
            @click="clicked">
            <template v-slot:more="props">
                <v-btn v-if="can('admin')" color="accent" class="mr-4" @click="editPage({})" >Add</v-btn>
                <vu-input
                    v-model="page.showNested"
                    :inset="false"
                    suffix="Show Nested"
                    class="mr-3"
                    type="switch"
                    hide-details
                    @change="changeShowNested" />
            </template>
        </vu-table>

        <vu-panel v-model="page.showPageEdit" :widths="['550px']" @close="editPage">
            <PageEdit :view="page.view" @input="editPage" />
        </vu-panel>
    </div>
</template>

<style>
.page-list {
    margin-bottom: 40px;
    .table-data {
        i {
            color: rgb(var(--v-theme-primary)) !important;
        }
    }
    .vu-table {
        .v-btn {
            vertical-align: top;
        }
        .vu-input {
            display: inline-block;
            position: relative;
        }
    }
    .more {
        height: 36px;
    }
}
</style>
